import React from 'react';

import { useSelector } from 'react-redux';

import {
    Typography,
    WDSButton,
} from 'wanderu-components';
import { localize, selectCurrency } from 'wanderu-i18n';
import type { I18nMoneyFunction } from 'wanderu-i18n';

import { ReturnButton } from './ReturnButton';
import { TripDetails } from './TripDetails';
import {
    useFormattedTripsSelector,
    useItineraryInfoSelector,
} from '../hooks';
import events from './events';

import styles from './TripInfo.scss';

import type { TFunction } from 'i18next';

interface TripInfoProps {
    i18nMoney: I18nMoneyFunction,
    itineraryId: string,
    t: TFunction,
    togglePriceBreakdownModal: () => void,
}

function TripInfo({
    itineraryId,
    i18nMoney,
    togglePriceBreakdownModal,
    t,
}: TripInfoProps) {
    const {
        outboundTrips,
        returnTripId,
        returnTrips,
        tripPricing,
    } = useItineraryInfoSelector(itineraryId);
    const currency = useSelector(selectCurrency);

    const totalPrice = i18nMoney(tripPricing).getTotalPrice();
    const totalPriceInUSD = i18nMoney(totalPrice, currency).setCurrency('USD').getConvertedPrice().toFixed(2);
    const trips = [...outboundTrips, ...returnTrips];

    const formattedTrips = useFormattedTripsSelector({ i18nMoney, trips });

    const onClick = () => {
        events.priceBreakdownButtonClicked({
            returnTripId,
            totalPriceInUSD,
            trips: formattedTrips,
        });

        togglePriceBreakdownModal();
    };

    return (
        <div
            className={styles.container}
            data-id="trip-info"
        >
            <TripDetails legs={outboundTrips} />

            <hr className={styles.hr} />

            {returnTripId
                ? <TripDetails legs={returnTrips} isReturnTrip />
                : <ReturnButton />
            }

            <hr className={styles.hr} />

            <div
                className={styles.row}
                data-id="total-price"
            >
                <Typography className={styles.text} variant="subtitle">{t('tripTotal')}</Typography>
                <Typography className={styles.price} variant="h5">
                    {i18nMoney.format(totalPrice)}
                </Typography>
            </div>

            <WDSButton
                className={styles.button}
                onClick={onClick}
                type="button"
                variant="unstyled"
            >
                {t<string>('price-breakdown')}
            </WDSButton>
        </div>
    );
}

export { TripInfo };
export default localize(TripInfo, ['i18nMoney', 't'], 'baseApp');
